import React, { useContext, useEffect, useState } from "react";
import { useQuery, useMutation } from "@apollo/client";
import { Tabs, LegacyCard as Card, Layout } from "@shopify/polaris";

// import hoc
import { PrivateContext } from "lib/context";
import { withFeature, withErrorBoundary } from "lib/hoc";

// import form lib
import { baseHelper, errorHelper } from "lib/helpers";
import constant from "lib/constant/constant";
import { ResourceList, Banner } from "lib/components";

// import GQL
import { FETCH_PRODUCT_LIST } from "app/advanceVendor/apollo/queries";
import BULK_PRODUCT from "app/advanceVendor/apollo/mutations";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import productListCMS from "./cms/productList";

// import subFeature
import ProductListView from "./subFeatures/productListView";
import MoreFilters from "./moreFilters/moreFilters";

const AdvanceOperatorProductList = () => {
  const { cms } = useContext(PrivateContext);
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const prevAppliedFilter = baseHelper.queryParamsFromLocation(location);
  const { tabs, sortOptions, resourceName } = productListCMS(cms);
  const selectedSort = prevAppliedFilter.sort_name && `${prevAppliedFilter.sort_name}_${prevAppliedFilter.sort_order}`;

  // react state
  const [queryValue, setQueryValue] = useState(prevAppliedFilter.search || null);
  const [selected, setSelected] = useState(parseInt(prevAppliedFilter.tab, 10) || 0);
  const [selectedLimit, setSelectedLimit] = useState(parseInt(prevAppliedFilter.perPage, 10) || 10);
  const [currentPage, setCurrentPage] = useState(parseInt(prevAppliedFilter.page, 10) || 1);
  const [productList, setProductList] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [totalProductCount, setTotalProductCount] = useState(0);
  const [sortValue, setSortValue] = useState(selectedSort || constant.CREATED_DESC);
  const [selectedItemStatus, setSelectedItemStatus] = useState(false);
  const [taggedWith, setTaggedWith] = useState(prevAppliedFilter.list_search || null);
  const [selectedFilter, setSelectedFilter] = useState(prevAppliedFilter.list_filter || null);
  const [dataToFetch, setDataToFetch] = useState({
    search: prevAppliedFilter.search,
    list_filter: prevAppliedFilter.list_filter,
    list_search: prevAppliedFilter.list_search,
    filter: prevAppliedFilter.filter,
    perPage: selectedLimit,
    page: currentPage,
    sort_name: prevAppliedFilter.sort_name || constant.CREATED_AT,
    sort_order: prevAppliedFilter.sort_order || constant.DESC,
    vendorId: id,
  });
  const [banner, setBanner] = useState({
    isOpen: false,
    status: "",
    title: "",
  });

  const inputData = {
    search: dataToFetch.search,
    list_filter: dataToFetch.list_filter,
    list_search: dataToFetch.list_search,
    filter: dataToFetch.filter,
    sort_name: dataToFetch.sort_name,
    sort_order: dataToFetch.sort_order,
    page: parseInt(dataToFetch.page, 10),
    perPage: parseInt(dataToFetch.perPage, 10),
    vendorId: dataToFetch.vendorId,
  };
  // GQL:Operations
  const { error: errorProductList, loading: loadingProductList, data: dataProductList } = useQuery(FETCH_PRODUCT_LIST, {
    variables: {
      input: inputData,
    },
  });

  const [bulkProductActions, { loading: loadingBulkProduct }] = useMutation(BULK_PRODUCT, {
    refetchQueries: [
      {
        query: FETCH_PRODUCT_LIST,
        variables: {
          input: inputData,
        },
      },
    ],
  });

  useEffect(() => {
    setSelectedItems([]);
    const productListResponseResponseData = baseHelper.getResponseData(dataProductList, constant.gql.GET_PRODUCT_LIST);
    const productListResponseResponseError = baseHelper.getResponseError(
      dataProductList,
      constant.gql.GET_PRODUCT_LIST
    );
    if (productListResponseResponseData) {
      setProductList(productListResponseResponseData.productList);
      setTotalProductCount(productListResponseResponseData.count);
    }
    if (productListResponseResponseError) {
      setBanner({ status: constant.CRITICAL, title: productListResponseResponseError, isOpen: true });
    }
  }, [dataProductList]);

  useEffect(() => {
    if (errorProductList) {
      setBanner({ status: constant.CRITICAL, title: errorHelper.parse(errorProductList), isOpen: true });
    }
  }, [errorProductList]);

  useEffect(() => {
    const filterApplied = baseHelper.queryParamsFromLocation(location);
    setDataToFetch({ ...dataToFetch, ...filterApplied });
  }, [location.search]);

  const handleQueryValueChange = (value) => {
    setQueryValue(value);
    baseHelper.setUrl(location, navigate, { search: value });
  };

  const handleQueryValueRemove = () => {
    setQueryValue(null);
    baseHelper.setUrl(location, navigate, { search: "" });
  };

  const handleSelectLimit = (value) => {
    setSelectedLimit(parseInt(value, 10));
    baseHelper.setUrl(location, navigate, { perPage: value });
  };

  const handlePage = (page) => {
    setCurrentPage(parseInt(page, 10));
    baseHelper.setUrl(location, navigate, { page });
  };

  const handleTabChange = (selectedTabIndex) => {
    const tabName = tabs[selectedTabIndex].value;
    setSelected(selectedTabIndex);
    baseHelper.setUrl(location, navigate, { filter: tabName, tab: selectedTabIndex });
  };

  const sortFilter = (selectedItem) => {
    const sort = selectedItem.split("_");
    if (sort && sort.length === 2) {
      setSortValue(selectedItem);
      baseHelper.setUrl(location, navigate, { sort_name: sort[0], sort_order: sort[1] });
    }
  };
  const bulkAction = async (
    eventKey,
    deleteFromShopify = false,
    id = false,
    reason = "",
    selectedStatusItem = false
  ) => {
    if (selectedStatusItem) {
      setSelectedItemStatus(selectedStatusItem);
    }
    let formData = {};
    formData = {
      key: eventKey,
      ids: selectedItems,
      deleteFromShopify,
      reason,
    };
    if (id) {
      formData.ids = [id];
    }
    try {
      const response = await bulkProductActions({
        variables: { input: { ...formData } },
      });
      setSelectedItems([]);
      const responseData = baseHelper.getResponseData(response.data, constant.gql.BULK_PRODUCT);
      const responseDataError = baseHelper.getResponseError(response.data, constant.gql.BULK_PRODUCT);
      if (responseData) {
        let title = cms("message.productList.requestSuccess");
        if (
          selectedItemStatus.toLowerCase() === constant.APPROVED.toLowerCase() &&
          eventKey === constant.providerList.REJECT
        ) {
          title = cms("message.productList.requestRejectSuccess");
        }
        setBanner({ isOpen: true, title, status: constant.SUCCESS });
        setSelectedItemStatus(false);
      }
      if (responseDataError) {
        let title = responseDataError;
        if (
          selectedItemStatus.toLowerCase() !== constant.NEW.toLowerCase() &&
          eventKey === constant.providerList.APPROVE
        ) {
          title = cms("message.productList.alreadyPush");
        }
        setBanner({ isOpen: true, title, status: constant.CRITICAL });
        setSelectedItemStatus(false);
      }
    } catch (exception) {
      setBanner({ isOpen: true, status: constant.CRITICAL, title: errorHelper.parse(exception) });
    }
    setSelectedItemStatus(false);
  };

  const handleTaggedWithRemove = () => {
    setTaggedWith("");
    setSelectedFilter("");
    baseHelper.setUrl(location, navigate, { list_search: "", list_filter: "", page: 1 });
  };

  const handleClearAll = () => {
    handleQueryValueRemove();
    handleTaggedWithRemove();
  };

  function disambiguateLabel(key, value) {
    switch (key) {
      case constant.providerList.TAGGED_WITH:
        return `${value}`;
      default:
        return value;
    }
  }

  function isEmpty(value) {
    if (Array.isArray(value)) {
      return value.length === 0;
    }
    return value === "" || value == null;
  }

  const appliedFilterArrObj = [
    {
      key: constant.providerList.TAGGED_WITH,
      label: disambiguateLabel(constant.providerList.TAGGED_WITH, taggedWith),
      onRemove: handleTaggedWithRemove,
    },
  ];
  const appliedFilters = !isEmpty(taggedWith) ? appliedFilterArrObj : [];

  const filters = [
    {
      // key: t("taggedWith"),
      label: cms("label.productList.filterBy"),
      filter: (
        <MoreFilters
          dataToFetch={dataToFetch}
          setDataToFetch={setDataToFetch}
          taggedWith={taggedWith}
          setTaggedWith={setTaggedWith}
          selectedFilter={selectedFilter}
          setSelectedFilter={setSelectedFilter}
        />
      ),
      shortcut: false,
    },
  ];
  const renderItem = (item, _, keyIndex) => {
    return <ProductListView item={item} keyIndex={keyIndex} bulkAction={bulkAction} loading={loadingBulkProduct} />;
  };
  return (
    <>
      <Layout>
        {banner.isOpen && (
          <>
            <Layout.Section>
              <Banner
                isOpen={banner.isOpen}
                status={banner.status}
                title={banner.title}
                onDismiss={() => setBanner({ isOpen: false, title: "", status: "" })}
              />
            </Layout.Section>
            <br />
          </>
        )}
        <Layout.Section>
          <Card>
            <Tabs tabs={tabs} selected={selected} onSelect={handleTabChange}>
              <ResourceList
                resourceName={resourceName}
                items={productList || []}
                idForItem={(products) => {
                  const { productId: id, status } = products;
                  setSelectedItemStatus(status);
                  return id;
                }}
                selectedItems={selectedItems}
                renderItem={renderItem}
                loading={loadingProductList}
                onQueryChange={handleQueryValueChange}
                onQueryClear={handleQueryValueRemove}
                handleClearAll={handleClearAll}
                queryValue={queryValue}
                sortValue={sortValue}
                sortOptions={sortOptions}
                onSortChange={sortFilter}
                count={totalProductCount}
                page={currentPage}
                perPage={selectedLimit}
                setPerPage={handleSelectLimit}
                setPage={handlePage}
                filters={filters}
                appliedFilters={appliedFilters}
              />
            </Tabs>
          </Card>
        </Layout.Section>
      </Layout>
    </>
  );
};

export default withFeature(withErrorBoundary(AdvanceOperatorProductList), {
  feature: constant.ADVANCE_VENDOR_PROFILE,
  // subFeature: constant.ADVANCE_VENDOR_PROFILE,
});
